import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { NavRailItems } from '../../models/menu.interface';
import { APP_CONFIG } from './../../../app/app.config';
import { HttpClient } from '@angular/common/http';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { filter, map, take, tap } from 'rxjs/operators';
import { MENU_ITEM_NODE } from '../../../../graphQL/node-query-strings';

@Injectable({
    providedIn: 'root',
})
export class MenusService {
    public menus$ = new BehaviorSubject<NavRailItems[]>([]);
    private apollo!: ApolloBase;

    private readonly appConfig = inject(APP_CONFIG);
    private readonly http = inject(HttpClient);
    private readonly apolloProvider = inject(Apollo);

    getNavigation(): void {
        if (!this.appConfig.useJson) {
            this.apollo = this.apolloProvider.use('cms');
            this.getApolloMenuData();
        }

        if (this.appConfig.useJson) {
            this.getJsonMenuData();
        }
    }

    getJsonMenuData(): void {
        this.http
            .get(`${this.appConfig.assetsPath}${this.appConfig.json.menu}`)
            .pipe(
                map((data: any) =>
                    this.organize(data?.data?.menu?.menuItems?.nodes ?? []),
                ),
                tap((menus) => this.menus$.next(menus)),
            )
            .subscribe({
                error: (err) => {
                    console.error(err);
                },
            });
    }

    getApolloMenuData(): void {
        this.apollo
            .watchQuery({
                query: gql`{
                            menu(id: "${this.appConfig.menuRoot}") {
                              name
                              menuItems(where: {parentDatabaseId: 0}, first: 999) {
                                nodes {
                                  ${MENU_ITEM_NODE}
                                }
                              }
                            }
                          }`,
            })
            .valueChanges.pipe(
                filter((result: any) => !result?.loading),
                take(1),
                map((result: any) =>
                    this.organize(result?.data?.menu?.menuItems?.nodes ?? []),
                ),
                tap((menus) => this.menus$.next(menus)),
            )
            .subscribe({
                error: (err) => {
                    console.error('error', err);
                },
            });
    }

    organize(nodes: any): NavRailItems[] {
        const nodesClone = structuredClone(nodes);
        const menus: any[] = [];

        nodesClone.forEach((node: any) => {
            let icon = node.adsMenu.icon;
            if (icon === 'action--home') {
                node.adsMenu.icon = 'navigation--home';
            }

            if (icon === 'action--back') {
                node.adsMenu.icon = 'navigation--back';
            }

            this.setMenu(node, menus);
        });

        return menus;
    }

    setMenu(node: any, menus: any) {
        const menu = new NavRailItems(node);
        menus.push(menu);

        if (node.childItems.nodes.length) {
            menu.children = [];
            node.childItems.nodes.forEach((child: any) => {
                menu.children?.push(new NavRailItems(child));
            });

            this.insertComponentsGetStarted(menu);
        }
    }

    private insertComponentsGetStarted(menu: NavRailItems): void {
        if (
            !menu.children?.length ||
            this.normalizeLabel(menu.name) !== 'components'
        ) {
            return;
        }

        const children = menu.children;
        const getStartedExists = children.some(
            (child) => this.normalizeLabel(child.name) === 'getting started',
        );

        if (getStartedExists) {
            return;
        }

        const overviewIndex = children.findIndex(
            (child) => this.normalizeLabel(child.name) === 'overview',
        );
        const appBarIndex = children.findIndex(
            (child) => this.normalizeLabel(child.name) === 'app bar',
        );

        if (
            overviewIndex < 0 ||
            appBarIndex < 0 ||
            overviewIndex >= appBarIndex
        ) {
            return;
        }

        const getStartedNode = {
            id: 'components-getting-started',
            label: 'Getting started',
            path: '/aveva-design-system/components/getting-started/',
            adsMenu: {
                icon: undefined,
                isseparator: false,
            },
        };

        children.splice(overviewIndex + 1, 0, new NavRailItems(getStartedNode));
    }

    private normalizeLabel(value: string | undefined): string {
        if (value === undefined) {
            return '';
        }

        return value.trim().toLowerCase();
    }
}
